import { CircleAlert, TriangleAlert } from 'lucide-react';
import { cn } from '../lib/cn.ts';
import { formatMoney, formatTokens } from '../lib/metric.ts';
import { fillTransform } from './Gauge.tsx';
import { Skeleton } from './Skeleton.tsx';

/** Fraction of budget where the meter turns amber. Mirrors the meter.warn-at token. */
export const METER_WARN_AT = 0.8;

export type BudgetLevel = 'none' | 'ok' | 'near' | 'over';

export function budgetLevel(cost: number | undefined, budget: number | undefined): BudgetLevel {
  if (budget == null || !(budget > 0) || cost == null) return 'none';
  const f = cost / budget;
  return f > 1 ? 'over' : f >= METER_WARN_AT ? 'near' : 'ok';
}

export interface CostMeterProps {
  /** What is being metered: "Session spend", "Qawl-Agents this month". */
  label: string;
  /** Spend in dollars. */
  cost?: number;
  tokens?: number;
  /** Budget in dollars. Without one, the meter shows figures only. */
  budget?: number;
  /** "this month", "today". */
  period?: string;
  /** Estimates (watched sessions, read from transcripts) say so. */
  estimated?: boolean;
  /** md: label row, figures and bar. sm: one line for rows and drawers. */
  size?: 'sm' | 'md';
  state?: 'loading' | 'full';
  className?: string;
}

const FILL = { none: 'bg-(--meter-fill)', ok: 'bg-(--meter-fill) prism-ray-progress', near: 'bg-(--meter-fill-warn)', over: 'bg-(--meter-fill-over)' };

/** Tokens and money against a budget. Near and over budget add an icon and words, never colour alone. */
export function CostMeter({ label, cost, tokens, budget, period, estimated, size = 'md', state = 'full', className }: CostMeterProps) {
  const level = budgetLevel(cost, budget);
  const fraction = level === 'none' ? 0 : Math.max(0, (cost ?? 0) / (budget ?? 1));
  const pct = Math.round(fraction * 100);
  const money = cost != null ? formatMoney(cost) : null;
  const tokenText = tokens != null ? formatTokens(tokens) : null;

  if (state === 'loading') {
    return (
      <div className={cn('flex min-w-0 flex-col gap-2', className)} role="status" aria-label={`Loading ${label}`}>
        {size === 'md' && <Skeleton className="h-3 w-24" />}
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-(--meter-track-height) w-full" />
      </div>
    );
  }

  const status =
    level === 'over' ? (
      <span className="inline-flex items-center gap-1 font-medium text-status-danger">
        <CircleAlert className="size-3.5 shrink-0" aria-hidden />
        Over budget by {formatMoney((cost ?? 0) - (budget ?? 0))}
      </span>
    ) : level === 'near' ? (
      <span className="inline-flex items-center gap-1 font-medium text-status-attention">
        <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
        {pct}% of budget
      </span>
    ) : level === 'ok' ? (
      <span className="text-fg-subtle num">{pct}% of budget</span>
    ) : (
      <span className="text-fg-subtle">No budget set</span>
    );

  const bar =
    level === 'none' ? null : (
      <div
        role="meter"
        aria-label={`${label}: ${money ?? 'no spend'} of ${formatMoney(budget ?? 0)} budget`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(pct, 100)}
        aria-valuetext={`${pct}% of budget${level === 'over' ? ', over budget' : ''}`}
        className="relative h-(--meter-track-height) w-full overflow-hidden rounded-(--meter-radius) bg-(--meter-track)"
      >
        {/* The fill moves by transform, never width: compositor only, and it settles without overshoot. */}
        <div
          data-fill=""
          className={cn('absolute inset-0 rounded-(--meter-radius) transition-transform duration-(--duration-slow) ease-settle', FILL[level])}
          style={{ transform: fillTransform(fraction) }}
        />
      </div>
    );

  if (size === 'sm') {
    return (
      <div className={cn('flex min-w-0 items-center gap-2 text-caption', className)}>
        <span className="shrink-0 font-medium text-fg num">{money ?? tokenText ?? 'No spend'}</span>
        {bar && <div className="min-w-12 flex-1">{bar}</div>}
        {level === 'over' || level === 'near' ? status : bar ? <span className="shrink-0 text-fg-subtle num">{pct}%</span> : null}
      </div>
    );
  }

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className="flex min-w-0 items-baseline justify-between gap-2 text-caption">
        <span className="truncate text-fg-muted">
          {label}
          {period && <span className="text-fg-subtle"> {period}</span>}
        </span>
        {estimated && <span className="shrink-0 text-fg-subtle">Estimated</span>}
      </div>
      <div className="flex min-w-0 items-baseline gap-1.5">
        <span className="text-lead font-semibold text-fg num">{money ?? 'No spend yet'}</span>
        {budget != null && budget > 0 && <span className="text-ui text-fg-subtle num">of {formatMoney(budget)}</span>}
        {tokenText && <span className="ml-auto shrink-0 text-ui text-fg-muted num">{tokenText}</span>}
      </div>
      {bar}
      <div className="text-caption">{status}</div>
    </div>
  );
}
